import React, { useEffect, useState, memo } from 'react';
import Swal from '../librarianSwal';
import { useNavigate } from 'react-router-dom';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import '../Reports/ReportDashboard.css';
import '../lib-page-header.css';
import '../lib-typography.css';

import {
  getBooksByLibrary,
  getBookCopiesByLibrary,
  getReservationsByLibrary,
  getIssuesByLibrary,
  getFinesByLibrary,
  getMembersByLibrary,
  getErrorMessage
} from '../../api/api';

// Hoisted to module scope + memoized: declaring this inside LibrarianDashboard
// made it a brand-new component type on every render, forcing React to
// unmount/remount all 10 stat cards on every dashboard refetch instead of
// just diffing their (mostly-primitive) props.
const Card = memo(({ title, value, accent, path, icon, navigate }) => (
  <div
    className="report-card"
    style={{ '--accent': accent, cursor: path ? 'pointer' : 'default' }}
    onClick={() => path && navigate(path)}
  >
    {icon && (
      <span
        className="report-card__icon"
        style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}
      >
        <i className={icon} />
      </span>
    )}
    <span className="report-card__title lib-card-title">{title}</span>
    <span className="report-card__value">{value}</span>
  </div>
));
Card.displayName = 'DashboardStatCard';

const LibrarianDashboard = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();
  const libraryId = useActiveLibraryId();

  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [stats, setStats] = useState({
    books: 0,
    copies: 0,
    availableCopies: 0,
    issuedCopies: 0,
    reservations: 0,
    pendingRequests: 0,
    issues: 0,
    activeIssues: 0,
    fines: 0,
    unpaidFines: 0,
    members: 0
  });

  const fetchDashboard = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);

      const [
        books,
        copies,
        reservations,
        issues,
        fines,
        members
      ] = await Promise.all([
        getBooksByLibrary(libraryId),
        getBookCopiesByLibrary(libraryId),
        getReservationsByLibrary(libraryId),
        getIssuesByLibrary(libraryId),
        getFinesByLibrary(libraryId),
        getMembersByLibrary(libraryId)
      ]);

      setStats({
        books: books?.length || 0,
        copies: copies?.length || 0,
        availableCopies: copies?.filter((c) => c.availability_status === 'available').length || 0,
        issuedCopies: copies?.filter((c) => c.availability_status === 'issued').length || 0,
        reservations: reservations?.length || 0,
        pendingRequests: reservations?.filter((r) => r.status === 'requested').length || 0,
        issues: issues?.length || 0,
        activeIssues: issues?.filter((i) => i.issue_status === 'issued').length || 0,
        fines: fines?.length || 0,
        unpaidFines: fines?.filter((f) => (f.fine_status || f.status) === 'unpaid').length || 0,
        members: members?.length || 0
      });
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header lib-page-header--roomy">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon"><i className="fas fa-tachometer-alt" /></div>
          <div>
            <h3 className="lib-page-header__title">Librarian Dashboard</h3>
            <div className="lib-page-header__subtitle">Library overview and quick actions</div>
          </div>
        </div>
      </div>

      {showLoader ? (
        <Loader />
      ) : (
        <>
          <div className="report-grid">
            <Card title="Books" value={stats.books} accent="#6f8fe6" path={`${sectionBase}/books`} icon="fas fa-book" navigate={navigate} />
            <Card title="Total Copies" value={stats.copies} accent="#7c5cfc" path={`${sectionBase}/book-copies`} icon="fas fa-layer-group" navigate={navigate} />
            <Card title="Available Copies" value={stats.availableCopies} accent="#22b07d" path={`${sectionBase}/book-copies`} icon="fas fa-check-circle" navigate={navigate} />
            <Card title="Issued Copies" value={stats.issuedCopies} accent="#f5a524" path={`${sectionBase}/issues`} icon="fas fa-book-reader" navigate={navigate} />
            <Card title="Parent Requests" value={stats.reservations} accent="#d6669a" path={`${sectionBase}/reservations`} icon="fas fa-inbox" navigate={navigate} />
            <Card title="Pending Requests" value={stats.pendingRequests} accent="#f59e0b" path={`${sectionBase}/reservations`} icon="fas fa-hourglass-half" navigate={navigate} />
            <Card title="Active Issues" value={stats.activeIssues} accent="#0ea5e9" path={`${sectionBase}/issues`} icon="fas fa-exchange-alt" navigate={navigate} />
            <Card title="Members" value={stats.members} accent="#2dabb5" path={`${sectionBase}/members`} icon="fas fa-users" navigate={navigate} />
            <Card title="Fines" value={stats.fines} accent="#ef5350" path={`${sectionBase}/fines`} icon="fas fa-coins" navigate={navigate} />
            <Card title="Unpaid Fines" value={stats.unpaidFines} accent="#f97362" path={`${sectionBase}/fines`} icon="fas fa-exclamation-triangle" navigate={navigate} />
          </div>

          <div className="quick-actions">
            <div className="quick-actions__head">
              <h4>Quick Actions</h4>
              <span>Jump straight to a common task</span>
            </div>

            <div className="quick-actions__grid">
              <button
                className="qa-btn"
                style={{ '--qa': '#6f8fe6' }}
                onClick={() => navigate(`${sectionBase}/books/add`)}
              >
                <span className="qa-btn__icon">＋</span>
                Add Book
              </button>

              <button
                className="qa-btn"
                style={{ '--qa': '#7c5cfc' }}
                onClick={() => navigate(`${sectionBase}/book-categories`)}
              >
                <span className="qa-btn__icon">☷</span>
                Book Categories
              </button>

              <button
                className="qa-btn"
                style={{ '--qa': '#22b07d' }}
                onClick={() => navigate(`${sectionBase}/issues/create`)}
              >
                <span className="qa-btn__icon">↪</span>
                Issue Book
              </button>

              <button
                className="qa-btn"
                style={{ '--qa': '#f5a524' }}
                onClick={() => navigate(`${sectionBase}/reservations`)}
              >
                <span className="qa-btn__icon">☑</span>
                Parent Requests
              </button>

              <button
                className="qa-btn"
                style={{ '--qa': '#ef5350' }}
                onClick={() => navigate(`${sectionBase}/payments`)}
              >
                <span className="qa-btn__icon">₹</span>
                Payments
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LibrarianDashboard;